/**
 * Tenant identity for the browser.
 *
 * Copied verbatim into each frontend, exactly as `backend/identity/` is copied
 * into each backend. Anything that differs between apps belongs in the app's
 * own wiring, not in here.
 *
 * The build is brand-agnostic: nothing about a school is compiled in. The host
 * the page was served from decides which school it is, the backend answers with
 * that school's identity, and this module applies it. That is why a logo, a
 * name or a colour must never be imported as a module asset - a bundled logo
 * ships one client's brand to every client that runs the same build.
 *
 * Neutral on failure. If identity cannot be loaded the app stays grey and
 * unnamed, which reads as unconfigured. It must never fall back to a particular
 * school, because a wrong brand is far harder to notice than a missing one.
 */

/** Where a cached copy lives, so a reload does not flash neutral first. */
const CACHE_KEY = 'spark.identity.v1';

/**
 * What the app shows when it has no identity: no school name, no logo, and a
 * neutral grey that belongs to nobody.
 */
export const NEUTRAL = Object.freeze({
  name: '',
  shortName: '',
  logoUrl: '',
  faviconUrl: '',
  phone: '',
  supportEmail: '',
  currency: '',
  latitude: null,
  longitude: null,
  primary: '#5a6875',
  secondary: '#eef1f4',
  accent: '#5a6875',
  headerText: '#ffffff',
  textColor: '#1b2530',
  locale: 'en',
  resolved: false,
});

/**
 * Flatten the backend's 31-key document into the handful of things a frontend
 * actually renders. Keeping this mapping in one place means a change to the
 * contract is absorbed here rather than in every component.
 */
export function normalise(raw) {
  if (!raw || typeof raw !== 'object') return { ...NEUTRAL };

  const palette = raw.palette || {};
  const theme = raw.theme || {};

  const pick = (...values) => {
    for (const v of values) {
      if (typeof v === 'string' && v.trim() !== '') return v.trim();
    }
    return '';
  };

  return {
    name: pick(raw.name),
    shortName: pick(raw.shortName, raw.name),
    nameArabic: pick(raw.nameArabic),
    logoUrl: pick(raw.logoUrl, raw.printLogoUrl),
    faviconUrl: pick(raw.faviconUrl, raw.logoUrl),
    primary: pick(theme.primary, palette.primary_color, NEUTRAL.primary),
    primaryDark: pick(theme.primaryDark, palette.primary_color, NEUTRAL.primary),
    secondary: pick(palette.secondary_color, NEUTRAL.secondary),
    accent: pick(theme.accent, palette.accent_color, NEUTRAL.accent),
    headerText: pick(palette.header_text_color, NEUTRAL.headerText),
    textColor: pick(palette.text_color, NEUTRAL.textColor),
    backgroundPrimary: pick(palette.background_p_color, '#ffffff'),
    backgroundSecondary: pick(palette.background_s_color, '#f5f5f5'),
    // Contact details are identity too: a portal that prints a phone number or a
    // support address is telling a parent who to call, and a hardcoded one sends
    // every school's parents to whichever school was hardcoded.
    phone: pick(raw.phone),
    supportEmail: pick(raw.supportEmail),
    currency: pick(raw.currency),
    // Where the school is. Null unless it resolves: a map must leave the school
    // off rather than drop its pin on a default, and 0,0 is a real place.
    latitude: coordinate(raw.latitude, 90),
    longitude: coordinate(raw.longitude, 180),
    locale: pick(raw.locale, 'en'),
    domain: pick(raw.domain, raw.tenantId),
    urls: raw.urls || {},
    resolved: Boolean(pick(raw.name)),
  };
}

/**
 * One coordinate as a number, or null.
 *
 * The backend already casts and range-checks these, but the same document is
 * also read from sessionStorage and from older deployments whose backend still
 * hands every column back as a string. Re-checking here costs nothing and keeps
 * a quoted "29.985" from reaching Google Maps, which silently ignores it.
 */
function coordinate(value, limit) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || Math.abs(n) > limit) return null;
  return n;
}

/* ------------------------------------------------------------------ colour */

function hexToRgb(hex) {
  const clean = String(hex).replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const to = (n) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Mix towards white (amount > 0) or black (amount < 0). */
function shade(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const target = amount >= 0 ? 255 : 0;
  const t = Math.abs(amount);
  return rgbToHex({
    r: rgb.r + (target - rgb.r) * t,
    g: rgb.g + (target - rgb.g) * t,
    b: rgb.b + (target - rgb.b) * t,
  });
}

/**
 * A Tailwind-shaped 50-900 ramp derived from one brand colour.
 *
 * The apps were written against a fixed palette - `lightAqua` in one, Tailwind's
 * `blue` in the other - used across a hundred or so class names. Generating the
 * whole ramp lets those existing classes be re-pointed at the tenant's colour in
 * a single config change, instead of editing every component.
 */
export function brandScale(primary) {
  const base = hexToRgb(primary) ? primary : NEUTRAL.primary;
  return {
    50: shade(base, 0.95),
    100: shade(base, 0.9),
    200: shade(base, 0.75),
    300: shade(base, 0.6),
    400: shade(base, 0.3),
    500: base,
    600: shade(base, -0.15),
    700: shade(base, -0.3),
    800: shade(base, -0.45),
    900: shade(base, -0.6),
    950: shade(base, -0.75),
  };
}

/** "r g b" channels, the form Tailwind needs to apply an alpha modifier. */
export function channels(hex) {
  const rgb = hexToRgb(hex);
  return rgb ? `${rgb.r} ${rgb.g} ${rgb.b}` : '90 104 117';
}

/** Readable foreground for a given background, by perceived luminance. */
export function readableOn(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  const l = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return l > 0.6 ? '#1b2530' : '#ffffff';
}

/* -------------------------------------------------------------------- apply */

/**
 * Push identity into the document: CSS variables, title, and favicon.
 *
 * The CSS variables are what the Tailwind configs read, so every existing
 * brand-coloured class follows the tenant without being touched.
 */
export function applyIdentity(identity) {
  if (typeof document === 'undefined') return;

  const id = identity || NEUTRAL;
  const root = document.documentElement;
  const scale = brandScale(id.primary);

  // Each colour is published twice: as a plain hex for direct CSS use, and as
  // bare "r g b" channels. Tailwind needs the channel form so that opacity
  // modifiers - bg-brand/50 and the like - keep working once the colour is a
  // variable rather than a literal.
  Object.entries(scale).forEach(([step, value]) => {
    root.style.setProperty(`--brand-${step}`, value);
    root.style.setProperty(`--brand-${step}-rgb`, channels(value));
  });

  root.style.setProperty('--brand-primary-rgb', channels(id.primary));
  root.style.setProperty('--brand-secondary-rgb', channels(id.secondary));
  root.style.setProperty('--brand-accent-rgb', channels(id.accent));
  root.style.setProperty('--brand-on-primary-rgb', channels(readableOn(id.primary)));

  root.style.setProperty('--brand-primary', id.primary);
  root.style.setProperty('--brand-primary-dark', id.primaryDark || id.primary);
  root.style.setProperty('--brand-secondary', id.secondary);
  root.style.setProperty('--brand-accent', id.accent);
  root.style.setProperty('--brand-header-text', id.headerText);
  root.style.setProperty('--brand-text', id.textColor);
  root.style.setProperty('--brand-on-primary', readableOn(id.primary));

  if (id.name) {
    const base = document.title.split(' - ')[0] || document.title;
    document.title = `${base} - ${id.name}`;
  }

  if (id.faviconUrl) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = id.faviconUrl;
  }
}

/* --------------------------------------------------------------------- load */

/** Last known identity, so a reload paints the right brand immediately. */
export function readCache() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    return raw ? normalise(JSON.parse(raw)) : null;
  } catch (e) {
    return null;
  }
}

function writeCache(raw) {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(raw));
  } catch (e) {
    /* Private browsing or a full quota: the app works without the cache. */
  }
}

/**
 * Fetch this host's identity.
 *
 * @param {string} baseUrl The app's own backend, e.g. https://<host>/backend.
 * @returns {Promise<object>} Never rejects: an unreachable backend resolves to
 *          NEUTRAL, because a page that renders unbranded is a better failure
 *          than one that renders another school's brand.
 */
export async function loadIdentity(baseUrl) {
  const url = `${String(baseUrl).replace(/\/+$/, '')}/identity.php`;
  try {
    const response = await fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`identity ${response.status}`);

    const body = await response.json();
    const raw = body && body.data ? body.data : body;
    if (!raw || !raw.name) throw new Error('identity payload had no school name');

    writeCache(raw);
    return normalise(raw);
  } catch (error) {
    if (typeof console !== 'undefined') {
      console.warn('[identity] falling back to neutral:', error.message);
    }
    return { ...NEUTRAL };
  }
}
